import React from 'react';
import { motion } from 'framer-motion';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setSqlConfirmation, selectHasSqlCompleted } from '../reducers/onboardingSlice';

/**
 * Props for SQLConfirmationCheckbox component
 */
interface SQLConfirmationCheckboxProps {
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** Optional custom label text */
  label?: string;
  /** Optional additional CSS classes */
  className?: string;
}

/**
 * SQLConfirmationCheckbox Component
 *
 * Checkbox that integrates with Redux state to track whether the user has
 * confirmed running the SQL setup commands.
 *
 * @component
 * @example
 * ```tsx
 * <SQLConfirmationCheckbox
 *   label="I have run this SQL in Snowflake"
 *   disabled={loading}
 * />
 * ```
 */
export const SQLConfirmationCheckbox: React.FC<SQLConfirmationCheckboxProps> = ({
  disabled = false,
  label = 'I have run this SQL',
  className = '',
}) => {
  const dispatch = useAppDispatch();
  const hasSqlCompleted = useAppSelector(selectHasSqlCompleted);

  /**
   * Handle checkbox change
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(setSqlConfirmation(e.target.checked));
  };

  return (
    <motion.div
      className={`flex items-start ${className}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex h-6 items-center">
        <input
          id="sql-confirmation"
          name="sql-confirmation"
          type="checkbox"
          checked={hasSqlCompleted}
          onChange={handleChange}
          disabled={disabled}
          className="h-4 w-4 rounded border-gray-300 text-dataops-primary-blue focus:ring-2 focus:ring-dataops-primary-blue focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-150"
          aria-describedby="sql-confirmation-description"
        />
      </div>
      <div className="ml-3">
        <label htmlFor="sql-confirmation" className="text-sm text-primary-text cursor-pointer select-none">
          {label}
        </label>
        <p id="sql-confirmation-description" className="mt-1 text-xs text-secondary-text">
          Check this box after you&apos;ve successfully run all the setup SQL commands in your Snowflake account.
        </p>
      </div>
    </motion.div>
  );
};
